import {
  ApolloCache,
  DefaultContext,
  MutationFunctionOptions,
  MutationTuple,
} from '@apollo/client';
import { useEffect, useState } from 'react';
import {
  Exact,
  UpdateUserInput,
  UpdateUserMutation,
  UpdateUserMutationVariables,
  User,
  useGetUserQuery,
  useUpdateUserMutation,
} from './graphql/generated';
import { useUserProvider } from './user.provider';

const useCurrentUser = (): User | undefined => {
  const userProvider = useUserProvider();
  const [user, setUser] = useState<User | undefined>();

  const userId = userProvider?.userInfo?.userId;

  const { data, loading } = useGetUserQuery({
    skip: userId === undefined,
    variables: {
      input: {
        id: userId as string,
      },
    },
    fetchPolicy: 'cache-only',
  });

  useEffect(() => {
    if (data && !loading) {
      const { __typename, ...userProps } = data.getUser;
      setUser(userProps);
    }
  }, [data, loading]);

  return user;
};

const useCurrentUserProp = <T>(prop: keyof User): T | undefined => {
  const user = useCurrentUser();

  if (user) {
    return user[prop];
  }
};

/**
 * If you only need the current user's id, use this hook instead of
 * `useCurrentUserProp`.
 *
 * Using `useCurrentUserProp` always returns undefined for the first render
 * since the states default User is undefined.
 *
 * This is particularly noticeable when trying to retrieve cached data. A query
 * for cached data using the id from `useCurrentUserProp` will also always
 * return a value of undefined and could be conflated with the data not being
 * in the cache.
 */
const useCurrentUserId = (): string | undefined => {
  const userProvider = useUserProvider();
  return userProvider?.userInfo?.userId;
};

const useUpdateUser = (): MutationTuple<
  UpdateUserMutation,
  Exact<{
    input: Partial<User>;
  }>
> => {
  const [update, mutationResult] = useUpdateUserMutation();

  const updateUser = async (
    options?:
      | MutationFunctionOptions<
          UpdateUserMutation,
          Exact<{
            input: Partial<User>;
          }>,
          DefaultContext,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ApolloCache<any>
        >
      | undefined
  ) => {
    const {
      permissions: _permissions,
      unreadNotifications: _unreadNotifications,
      intercomUserHash: _intercomUserHash,
      activityAt: _activityAt,
      tenantId: _tenantId,
      ...input
    } = options?.variables?.input ?? {};

    const variables: UpdateUserMutationVariables = { input };

    const prunedOptions = { ...options, variables } as MutationFunctionOptions<
      UpdateUserMutation,
      Exact<{
        input: UpdateUserInput;
      }>,
      DefaultContext,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ApolloCache<any>
    >;

    return update(prunedOptions);
  };

  return [updateUser, mutationResult];
};

export { useCurrentUser, useCurrentUserProp, useCurrentUserId, useUpdateUser };
